<template>
    <div class="row">
        <div class="col pr-0 pl-2">
            <span v-if="date">
                <input v-if="create && !readOnly" type="date" v-model="value.date" class="form-control form-control-sm"/>
                <input v-else type="text" :value="formatDate(value.date)" class="form-control form-control-sm" :readonly="true"/>
            </span>
            <span v-else>
                <select v-if="create && !readOnly" v-model="value.week_day" class="form-control form-control-sm" :disabled="!create">
                    <option></option>
                    <option v-for="(name, identifier) in weekdays" :value="identifier">{{name}}</option>
                </select>
                <input v-else type="text" :value="weekdays[value.week_day]" class="form-control form-control-sm" :readonly="true"/>
            </span>
        </div>
        <div class="col pr-0 pl-2">
            <select v-if="!readOnly" v-model="value.is_available" class="form-control form-control-sm" @change="isAvailableChanged">
                <option v-if="create"></option>
                <option v-for="(status, value) in availableStatus" :value="value">{{status}}</option>
            </select>
            <input v-else type="text" :value="availableStatus[value.is_available]" class="form-control form-control-sm" :readonly="true"/>
        </div>
        <div v-if="!useCutOff" class="col pr-0 pl-2">
            <input type="text" v-model.trim="value.from_time" class="form-control form-control-sm" :placeholder="timePlaceholder" pattern="(|0?[0-9]|1[0-9]|2[0-3])[:\.]([0-5][0-9])" maxlength="5" :class="{'invalid': !isValid && isFormFilled}" :readonly="isTimeReadOnly"/>
        </div>
        <div class="col pr-0 pl-2">
            <input type="text" v-model.trim="value.to_time" class="form-control form-control-sm" :placeholder="timePlaceholder" pattern="(|0?[0-9]|1[0-9]|2[0-3])[:\.]([0-5][0-9])" maxlength="5" :class="{'invalid': !isValid && isFormFilled}" :readonly="isTimeReadOnly"/>
        </div>
        <div class="col pr-0 pl-2">
            <input v-if="!date" type="text" v-model.trim="value.description" class="form-control form-control-sm" :readonly="readOnly"/>
            <select v-else-if="!readOnly" v-model="value.description" class="form-control form-control-sm">
                <option></option>
                <option v-for="description in serviceTimeReasons" :value="description.trim()">{{description}}</option>
            </select>
            <input v-else type="text" :value="value.description" class="form-control form-control-sm" :readonly="true"/>
        </div>
        <div v-if="usesCapacities" class="col pr-0 pl-2">
            <input type="number" v-model.number="value.max_capacity" class="form-control form-control-sm" />
        </div>

        <div class="col-2 px-2" v-if="$slots['default']">
            <slot/>
        </div>

        <div class="col-12 mt-2 mb-6" v-if="isFormFilled && !isValid && !readOnly">
            <OmniAlertInline variant="warning" size="sm">
                Nog niet alle velden zijn hier goed ingevuld.
            </OmniAlertInline>
        </div>

        <div class="col-12 mt-2 mb-6" v-if="!readOnly && isFormFilled && isValid && create">
            <OmniAlertInline variant="info" size="sm">
                Vergeet niet op de knop te drukken om de nieuwe tijden toe te voegen.
            </OmniAlertInline>
        </div>
    </div>
</template>

<script>
import {AVAILABLE_STATUS, WEEKDAYS} from "../../../helpers/consts";

export default {
    name: "LocationServiceTimeForm",

    props: {
        value: {type: Object},
        date: {type: Boolean, default: false},
        create: {type: Boolean, default: false},
        useCutOff: {type: Boolean, default: false},
        usesCapacities: {type: Boolean, default: false},
        readOnly: {type: Boolean, default: false},
    },

    data() {
        return {}
    },

    computed: {
        isTimeReadOnly() {
            return this.readOnly || this.value.is_available.toString() === '0'
        },
        weekdays() {
            return WEEKDAYS
        },
        availableStatus() {
            return AVAILABLE_STATUS
        },
        timePlaceholder() {
            return !this.isTimeReadOnly && (!this.useCutOff || this.value.is_available.toString() === '1') ? '12:30' : ''
        },
        isFormFilled() {
            return this.value.week_day || this.value.date || this.value.is_available.toString() === '1' || this.value.from_time || this.value.to_time
        },
        isValid() {
            // If the service is "open": to_time is required
            if (this.value.is_available.toString() === '1' && !this.checkValidTime(this.value.to_time)) {
                return false
            }

            if (!this.useCutOff) {
                // The service time must be either "open" or "closed", but not empty
                if (this.value.is_available.toString() === '') {
                    return false
                }

                // If the service is "open": from_time is required
                if (this.value.is_available.toString() === '1' && !this.checkValidTime(this.value.from_time)) {
                    return false
                }

                // If the service is "closed"
                if (this.value.is_available.toString() === '0') {
                    if (this.value.from_time || this.value.to_time) {
                        // If to_time is filled in: from_time is required as well
                        if (!this.checkValidTime(this.value.from_time) || !this.checkValidTime(this.value.to_time)) {
                            return false
                        }

                        if (this.isFromTimeBeforeToTime) {
                            return false
                        }
                    }

                    // Or both from_time and to_time must be empty
                    if (
                        (this.value.from_time && !this.value.to_time)
                        || (!this.value.from_time && this.value.to_time)
                    ) {
                        return false
                    }
                }
            }

            // Either the week_day or the date must be filled
            if (!this.value.week_day && !this.value.date) {
                return false
            }

            return true
        },
        isFromTimeBeforeToTime() {
            let fromTime = parseFloat(this.value.from_time.replace(':','.'))
            let toTime = parseFloat(this.value.to_time.replace(':','.'))

            return toTime <= fromTime
        },
        serviceTimeReasons() {
            let reasons = this.$store.getters.getFrontendParameterValue('service-times', 'service-time-reasons-' + this.$route.params.serviceTypeIdentifier);
            if (!reasons) {
                reasons = this.$store.getters.getFrontendParameterValue('service-times', 'service-time-reasons');
            }
            return reasons.split("\n");
        },
    },

    mounted() {
        this.$emit('validated', this.isValid)
    },

    methods: {
        overrideTimeValuesWhenClosed() {
            if (this.value.is_available.toString() === '0') {
                this.value.from_time = ''
                this.value.to_time = ''
            }
        },
        formatDate(date) {
            let parsedDate = new Date(date)
            return `${parsedDate.getDate()}-${parsedDate.getMonth() + 1}-${parsedDate.getFullYear()}`
        },
        checkValidTime(time) {
            if (time === null) {
                return false
            }

            return !!time.match(/^(|0?[0-9]|1[0-9]|2[0-3])[:\.]([0-5][0-9])$/)
        },
        isAvailableChanged() {
            this.overrideTimeValuesWhenClosed()
            this.$emit('serviceTimeUpdated')
        }
    },

    watch: {
        isValid(newValue) {
            this.$emit('validated', newValue)
        },
    }
}
</script>

<style scoped lang="scss">
input[readonly] {
    background: inherit !important;
    border: none !important;
}

input.invalid {
    border-color: red !important;
}

::-webkit-input-placeholder { /* Chrome/Opera/Safari */
    color: #ccc !important;
}
::-moz-placeholder { /* Firefox 19+ */
    color: #ccc !important;
}
:-ms-input-placeholder { /* IE 10+ */
    color: #ccc !important;
}
:-moz-placeholder { /* Firefox 18- */
    color: #ccc !important;
}
</style>
